import React, { useMemo, useState, useCallback } from 'react';
import { StyleSheet, View, Pressable, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import copyText from '@/constants/utils/copy_text';
import { useToastStore } from '@/store';
import MemoryActionsDropdown from '@/components/Dropdowns/MemoryActionsDropdown';

const isWeb = Platform.OS === 'web';

function OverflowTrigger({ colors, styles, label }) {
  return (
    <Pressable
      style={({ hovered }) => [
        styles.overflowButton,
        hovered && styles.overflowButtonHovered,
      ]}
      accessibilityRole="button"
      accessibilityLabel={label}
    >
      <Icon name="ellipsis" size={16} color={colors.text_color_primary} />
    </Pressable>
  );
}

function MemoryHero({
  memories = [],
  onOpenAdd,
  isAdding = false,
  isCreating = false,
  onOpenBuilder,
  onOpenImport,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const [isExporting, setIsExporting] = useState(false);
  const styles = useMemo(() => createStyles(colors, isXS), [colors, isXS]);

  const handleExport = useCallback(async () => {
    if (!memories.length) return;
    setIsExporting(true);
    try {
      const formatted = memories
        .map((m, idx) => {
          const source = m.source || (m.isAutoGenerated ? 'auto' : 'manual');
          const sourceLabel =
            source === 'explicit' ? t('settings.memory.sourceExplicit')
              : source === 'dream' ? t('settings.memory.sourceDream')
              : source === 'auto' ? t('settings.memory.sourceAuto')
              : source === 'import' ? t('settings.memory.sourceImport')
              : t('pages.memory.manualCount');
          return `${idx + 1}. [${sourceLabel}] ${m.content}`;
        })
        .join('\n\n');

      await copyText(`# Gab AI Memories (${memories.length} total)\n\n${formatted}\n`);
      addToast(
        t('pages.memory.allMemoriesExported').replace('%COUNT%', memories.length),
        'success'
      );
    } catch {
      addToast(t('common.failedToCopy') || 'Failed to copy memories', 'error');
    } finally {
      setTimeout(() => setIsExporting(false), 1500);
    }
  }, [memories, addToast, t]);

  return (
    <View style={styles.container}>
      <View style={styles.titleRow}>
        <View style={styles.titleCopy}>
          <Text tag="h1" weight="semibold" style={styles.headline}>
            {t('pages.memory.title')}
          </Text>
          <Text size="n" color="secondary" style={styles.subtitle}>
            {t('pages.memory.subtitle')}
          </Text>
        </View>
        <View style={styles.actions}>
          <Button
            text={isAdding ? t('common.cancel') : t('pages.memory.addMemory')}
            prependIcon={isAdding ? 'xmark' : 'plus'}
            variant="primary"
            size={isXS ? 'medium' : 'large'}
            isRound
            isDisabled={isCreating}
            onPress={onOpenAdd}
            iconColor={colors.solid_color_primary}
            textStyle={{ color: colors.solid_color_primary, fontWeight: '600' }}
            style={{ backgroundColor: colors.text_color_primary }}
          />
          <MemoryActionsDropdown
            TriggerComponent={() => (
              <OverflowTrigger
                colors={colors}
                styles={styles}
                label={t('pages.memory.moreOptions')}
              />
            )}
            onOpenBuilder={onOpenBuilder}
            onOpenImport={onOpenImport}
            onExport={handleExport}
            canExport={memories.length > 0}
            exportLabel={isExporting ? t('pages.memory.copied') : t('pages.memory.exportMemories')}
          />
        </View>
      </View>
    </View>
  );
}

const createStyles = (colors, isXS) =>
  StyleSheet.create({
    container: {
      width: '100%',
      paddingBottom: isXS ? 16 : 20,
    },
    titleRow: {
      flexDirection: isXS ? 'column' : 'row',
      alignItems: isXS ? 'stretch' : 'flex-start',
      justifyContent: 'space-between',
      gap: isXS ? 14 : 20,
    },
    titleCopy: {
      flex: 1,
      minWidth: 0,
      gap: 6,
    },
    headline: {
      fontSize: isXS ? 28 : 32,
      lineHeight: isXS ? 34 : 38,
      letterSpacing: -0.6,
      color: colors.text_color_primary,
    },
    subtitle: {
      maxWidth: 520,
      lineHeight: 22,
    },
    actions: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      flexShrink: 0,
    },
    overflowButton: {
      width: 40,
      height: 40,
      borderRadius: 999,
      alignItems: 'center',
      justifyContent: 'center',
      ...(isWeb ? { cursor: 'pointer' } : {}),
    },
    overflowButtonHovered: {
      backgroundColor: colors.solid_color_secondary,
    },
  });

export default React.memo(MemoryHero);
